import { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '../../icons';
import { Badge, Btn, FormRow, Field, Modal, ProgressBar, FormActions, SearchBar, MetricCard } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import TodoService from '../../api/todo';
import { toast } from 'react-hot-toast';
import { useSettings } from '../../context/SettingsContext';
import { formatDateByPreference, autoFormatDate } from '../../utils/formatters';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

export default function TodosScreen() {
  const { dateFormat, language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.todos?.[key] || translations.en?.todos?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [delTask, setDelTask] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [closedVisible, setClosedVisible] = useState(false);
  const [filterPriority, setFilterPriority] = useState("All");
  const [filterDue, setFilterDue] = useState("All");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", priority: "Medium", status: "Pending", due_date: "" });
  const [formErrors, setFormErrors] = useState({});
  const sf = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const [tasks, setTasks] = useState([]);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, title: val }));
    if (formErrors.title && val.trim()) {
      setFormErrors(prev => ({ ...prev, title: null }));
    }
  };

  const handleDueDateChange = (e) => {
    const rawVal = e.target.value;
    const formatted = autoFormatDate(rawVal, form.due_date, dateFormat);
    setForm(f => ({ ...f, due_date: formatted }));
  };

  const toInputDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    if (dateFormat === 'MM/DD/YYYY') return `${month}/${day}/${year}`;
    if (dateFormat === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
    return `${day}/${month}/${year}`;
  };

  const fromInputDate = (value) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (value.includes('/')) {
      const parts = value.split('/');
      if (parts.length === 3) {
        let [first, second, year] = parts;
        first = first.padStart(2, '0');
        second = second.padStart(2, '0');
        if (year.length === 2) year = `20${year}`;
        if (year.length === 4) {
          return dateFormat === 'MM/DD/YYYY' ? `${year}-${first}-${second}` : `${year}-${second}-${first}`;
        }
      }
    }
    return value;
  };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await TodoService.getTodos();
      if (res.success && res.data) {
        setTasks(res.data);
      }
    } catch (err) {
      if (err.status !== 403) {
        toast.error(t("toast_load_failed"));
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetched = useRef(false);
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetchTasks();
  }, [fetchTasks]);

  const COLS = ["Pending", "In Progress", "Completed"];
  const PCOL = { High: "red", Medium: "amber", Low: "blue" };

  const todayStr = new Date().toISOString().split('T')[0];
  const checkDueFilter = (taskDate) => {
    if (filterDue === "All") return true;
    if (!taskDate) return false;

    if (filterDue === "Today") return taskDate === todayStr;
    if (filterDue === "Overdue") return taskDate < todayStr;
    if (filterDue === "Upcoming") return taskDate > todayStr;
    return true;
  };

  const visibleTasks = tasks.filter(t =>
    (filterPriority === "All" || t.priority === filterPriority) &&
    (!filterAssignee || (t.assigned_to && t.assigned_to.toLowerCase().includes(filterAssignee.toLowerCase()))) &&
    checkDueFilter(t.due_date)
  );

  const hasActiveFilter = filterPriority !== "All" || filterAssignee !== "" || filterDue !== "All";

  const save = async () => {
    const trimmedTitle = (form.title || '').trim();
    if (!trimmedTitle) {
      const msg = t("val_title_req", "Title is required") || t("toast_title_req") || "Title is required";
      setFormErrors({ title: msg });
      toast.error(t("toast_title_req"));
      return;
    }
    setLoading(true);
    try {
      if (editTask) {
        await TodoService.updateTodo(editTask.id, { ...form, title: trimmedTitle, due_date: fromInputDate(form.due_date) });
        toast.success(t("toast_task_updated"));
      } else {
        await TodoService.createTodo({ ...form, title: trimmedTitle, due_date: fromInputDate(form.due_date) });
        toast.success(t("toast_task_added"));
      }
      setModal(false);
      setEditTask(null);
      setFormErrors({});
      setForm({ title: "", description: "", assigned_to: "", priority: "Medium", status: "Pending", due_date: "" });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.message || t("toast_save_failed"));
    } finally {
      setLoading(false);
    }
  };
  const startEdit = t => {
    setEditTask(t);
    setFormErrors({});
    setForm({
      title: t.title,
      description: t.description,
      assigned_to: t.assigned_to,
      priority: t.priority,
      status: t.status,
      due_date: t.due_date ? toInputDate(t.due_date) : ""
    });
    setModal(true);
  };
  const confirmDel = async () => {
    try {
      await TodoService.deleteTodo(delTask.id);
      toast.success(t("toast_task_deleted"));
      setDelTask(null);
      fetchTasks();
    } catch (err) {
      toast.error(t("toast_delete_failed"));
    }
  };
  const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const onDrop = async (e, col) => {
    e.preventDefault();
    if (dragId) {
      const task = tasks.find(t => t.id === dragId);
      if (task && task.status !== col) {
        setTasks(ts => ts.map(t => t.id === dragId ? { ...t, status: col } : t));
        try {
          await TodoService.updateTodo(dragId, { status: col });
        } catch (err) {
          toast.error(t("toast_update_status_failed"));
          fetchTasks();
        }
      }
    }
    setDragId(null); setDragOverCol(null);
  };

  const closedTasks = tasks.filter(t => t.status === "Closed");
  const visibleClosedTasks = closedTasks.filter(t =>
    (filterPriority === "All" || t.priority === filterPriority) &&
    (!filterAssignee || (t.assigned_to && t.assigned_to.toLowerCase().includes(filterAssignee.toLowerCase()))) &&
    checkDueFilter(t.due_date)
  );

  return (
    <div className="fade-up">
      {delTask && <ConfirmModal title={t("title_delete_task")} message={t("msg_delete_confirm", { title: delTask.title })} onConfirm={confirmDel} onCancel={() => setDelTask(null)} />}
      {modal && (
        <Modal title={editTask ? t("title_edit_task") : t("title_new_task")} onClose={() => { setModal(false); setEditTask(null); setFormErrors({}); }}>
          <FormRow>
            <Field 
              label={t("label_title")} 
              value={form.title} 
              onChange={handleTitleChange} 
              required 
              error={formErrors.title} 
            />
          </FormRow>
          <FormRow><Field label={t("label_description")} type="textarea" value={form.description} onChange={sf("description")} rows={2} /></FormRow>
          <FormRow cols={2}>
            <Field label={t("label_assigned_to")} value={form.assigned_to} onChange={sf("assigned_to")} />
            <Field 
              label={t("label_due_date")} 
              value={form.due_date} 
              onChange={handleDueDateChange} 
              placeholder={dateFormat} 
              maxLength={10}
              note={t("note_format", { format: dateFormat })} 
            />
          </FormRow>
          <FormRow cols={2}><Field label={t("label_priority")} type="select" value={form.priority} onChange={sf("priority")} options={[{ value: "Low", label: t("opt_low") }, { value: "Medium", label: t("opt_medium") }, { value: "High", label: t("opt_high") }]} /><Field label={t("label_status")} type="select" value={form.status} onChange={sf("status")} options={[{ value: "Pending", label: t("status_pending") }, { value: "In Progress", label: t("status_in_progress") }, { value: "Completed", label: t("status_completed") }, { value: "Closed", label: t("status_closed") }]} /></FormRow>
          <FormActions onSave={save} onCancel={() => { setModal(false); setEditTask(null); setFormErrors({}); }} label={editTask ? t("btn_update_task") : t("btn_add_task")} loading={loading} />
        </Modal>
      )}

      {/* Top Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <MetricCard
          label={t("status_pending")}
          value={tasks.filter(t => t.status === "Pending").length}
          icon="clock"
          accent="amber"
          onClick={() => {
            const el = document.getElementById('column-Pending');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
        <MetricCard
          label={t("status_in_progress")}
          value={tasks.filter(t => t.status === "In Progress").length}
          icon="refresh"
          accent="blue"
          onClick={() => {
            const el = document.getElementById('column-In Progress');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
        <MetricCard
          label={t("status_completed")}
          value={tasks.filter(t => t.status === "Completed").length}
          icon="check"
          accent="green"
          onClick={() => {
            const el = document.getElementById('column-Completed');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
        <MetricCard
          label={t("status_closed")}
          value={tasks.filter(t => t.status === "Closed").length}
          icon="lock"
          accent="purple"
          onClick={() => setClosedVisible(v => !v)}
        />
      </div>

      {/* Filter and Action Bar */}
      <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-3 sm:p-4 shadow-sm mb-6 space-y-3">
        {/* Search & Add Task Row */}
        <div className="flex items-center gap-2.5">
          <div className="flex-1 min-w-0">
            <SearchBar
              value={filterAssignee}
              onChange={v => setFilterAssignee(v)}
              placeholder={t("placeholder_search_assignee")}
              className="w-full bg-slate-50 dark:bg-slate-800/80 border-slate-200/60 dark:border-slate-700/60 shadow-none text-xs"
            />
          </div>
          <Btn
            variant="primary"
            icon="plus"
            className="shrink-0 h-10 px-4 rounded-xl text-xs sm:text-sm font-bold shadow-sm whitespace-nowrap"
            onClick={() => {
              setEditTask(null);
              setFormErrors({});
              setForm({ title: "", description: "", assigned_to: "", priority: "Medium", status: "Pending", due_date: "" });
              setModal(true);
            }}
          >
            {t("btn_add_task")}
          </Btn>
          {hasActiveFilter && (
            <button
              onClick={() => { setFilterPriority("All"); setFilterAssignee(""); setFilterDue("All"); }}
              className="shrink-0 h-10 px-3 text-xs text-red-600 font-bold hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all cursor-pointer bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 flex items-center gap-1.5"
              title={t("btn_clear_filters")}
            >
              <Icon name="x" size={14} />
              <span className="hidden sm:inline">{t("btn_clear_filters")}</span>
            </button>
          )}
        </div>

        {/* Priority & Due Date Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/70">
          {/* Priority */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2.5 min-w-0">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">
              {t("label_priority")}
            </span>
            <div className="grid grid-cols-4 p-1 bg-slate-100/90 dark:bg-slate-800/70 rounded-xl flex-1 min-w-0">
              {["All", "Low", "Medium", "High"].map(p => (
                <button
                  key={p}
                  onClick={() => setFilterPriority(p)}
                  className={`py-1.5 px-1 text-center text-xs font-bold rounded-lg transition-all cursor-pointer border-0 truncate ${
                    filterPriority === p
                      ? p === "High"
                        ? "bg-red-500 text-white shadow-sm"
                        : p === "Medium"
                        ? "bg-amber-500 text-white shadow-sm"
                        : p === "Low"
                        ? "bg-blue-500 text-white shadow-sm"
                        : "bg-white dark:bg-slate-700 text-ink dark:text-white shadow-sm"
                      : "bg-transparent text-slate-500 hover:text-ink dark:hover:text-white"
                  }`}
                >
                  {p === "All" ? t("opt_all") : p === "Low" ? t("opt_low") : p === "Medium" ? t("opt_medium") : t("opt_high")}
                </button>
              ))}
            </div>
          </div>

          {/* Due Date */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2.5 min-w-0">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">
              {t("label_due_date")}
            </span>
            <div className="grid grid-cols-4 p-1 bg-slate-100/90 dark:bg-slate-800/70 rounded-xl flex-1 min-w-0">
              {["All", "Today", "Overdue", "Upcoming"].map(d => (
                <button
                  key={d}
                  onClick={() => setFilterDue(d)}
                  className={`py-1.5 px-0.5 text-center text-[11px] sm:text-xs font-bold rounded-lg transition-all cursor-pointer border-0 truncate ${
                    filterDue === d
                      ? d === "Overdue"
                        ? "bg-red-500 text-white shadow-sm"
                        : d === "Today"
                        ? "bg-amber-500 text-white shadow-sm"
                        : d === "Upcoming"
                        ? "bg-blue-500 text-white shadow-sm"
                        : "bg-white dark:bg-slate-700 text-ink dark:text-white shadow-sm"
                      : "bg-transparent text-slate-500 hover:text-ink dark:hover:text-white"
                  }`}
                >
                  {d === "All" ? t("opt_all") : d === "Today" ? t("opt_today") : d === "Overdue" ? t("opt_overdue") : t("opt_upcoming")}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 relative pb-4">
        <div className="grid gap-4 flex-1 grid-cols-1 md:grid-cols-3">
          {COLS.map(col => (
            <div key={col} id={`column-${col}`}
              onDragOver={e => { e.preventDefault(); setDragOverCol(col); }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={e => onDrop(e, col)}
              className={`bg-slate-50/50 dark:bg-slate-900/50 rounded-2xl p-3 min-h-[400px] transition-all duration-150 relative border-t-4 flex flex-col shadow-sm ${col === "Pending" ? "border-t-amber-400" : col === "In Progress" ? "border-t-blue-400" : "border-t-emerald-400"} ${dragOverCol === col ? "bg-primary-light border-x-2 border-b-2 border-primary/30" : "border-x border-b border-slate-200 dark:border-slate-800"}`}>
              <div className="flex items-center justify-between mb-4 sticky top-0 z-10 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-sm py-2 px-1 -mx-1">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${col === "Pending" ? "bg-amber-500" : col === "In Progress" ? "bg-blue-500" : "bg-emerald-500"}`} />
                  <span className="text-sm font-black text-ink uppercase tracking-wider">{col === "Pending" ? t("status_pending") : col === "In Progress" ? t("status_in_progress") : col === "Completed" ? t("status_completed") : col}</span>
                  <span className="text-xs font-bold text-muted dark:text-slate-300 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded-full">{visibleTasks.filter(t => t.status === col).length}</span>
                </div>
              </div>
              <div className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden p-1 -m-1">
                {loading ? (
                  Array(3).fill(0).map((_, i) => (
                    <div key={`skel-${col}-${i}`} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 animate-pulse">
                      <div className="flex gap-2 mb-2">
                        <div className="w-3 h-3 bg-slate-200 dark:bg-slate-700 rounded-sm flex-shrink-0" />
                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
                      </div>
                      <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded w-5/6 mb-3" />
                      <div className="flex gap-1.5">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-12" />
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16" />
                      </div>
                    </div>
                  ))
                ) : (
                  visibleTasks.filter(t => t.status === col).map(task => (
                    <div key={task.id} draggable
                      onDragStart={e => onDragStart(e, task.id)}
                      className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 cursor-grab hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition-all group relative ${dragId === task.id ? "opacity-40 scale-95" : ""}`}>

                      {/* Left Drag Indicator */}
                      <div className="absolute left-1.5 top-0 bottom-0 flex items-center opacity-0 group-hover:opacity-40 transition-opacity">
                        <Icon name="drag" size={14} className="text-muted" />
                      </div>

                      <div className="flex items-start justify-between gap-2 mb-2 pl-3">
                        <p className="text-sm font-bold text-ink flex-1 leading-snug">{task.title}</p>
                        <div className="flex items-center gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <Btn
                            xs
                            icon="edit"
                            onClick={(e) => { e.stopPropagation(); startEdit(task); }}
                            className="!p-0 w-7 h-7 rounded-lg flex items-center justify-center"
                            title={t("btn_edit", "Edit")}
                          />
                          <Btn
                            xs
                            variant="danger"
                            icon="trash"
                            onClick={(e) => { e.stopPropagation(); setDelTask(task); }}
                            className="!p-0 w-7 h-7 rounded-lg flex items-center justify-center"
                            title={t("btn_del", "Delete")}
                          />
                        </div>
                      </div>

                      {task.description && <p className="text-xs text-muted mb-3 leading-relaxed line-clamp-2 pl-3">{task.description}</p>}

                      <div className="flex flex-col gap-2 pl-3 mt-3">
                        <div className="flex gap-2 items-center justify-between">
                          <Badge color={PCOL[task.priority] || "gray"} sm>{task.priority === "Low" ? t("opt_low") : task.priority === "Medium" ? t("opt_medium") : t("opt_high")}</Badge>
                          {task.due_date && <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1"><Icon name="calendar" size={12} /> {formatDateByPreference(task.due_date)}</span>}
                        </div>

                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                          <Badge color="gray" sm>{t("badge_operations")}</Badge>
                          {task.assigned_to ? (
                            <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-slate-700/50 px-2 py-1 rounded-full border border-slate-200 dark:border-slate-600">
                              <div className="w-4 h-4 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[8px] font-bold">
                                {task.assigned_to.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-bold text-ink uppercase tracking-wide truncate max-w-[80px]">{task.assigned_to}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">{t("msg_unassigned")}</span>
                          )}
                        </div>
                      </div>

                      {task.progress != null && <div className="mt-3 pl-3"><ProgressBar value={task.progress} showPct /></div>}
                    </div>
                  ))
                )}
                {!loading && visibleTasks.filter(t => t.status === col).length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center border-2 border-dashed border-slate-200 dark:border-slate-700/50 rounded-2xl h-full min-h-[160px] opacity-70">
                    <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3 text-slate-400">
                      <Icon name={col === "Completed" ? "check_circle" : col === "In Progress" ? "refresh" : "inbox"} size={24} />
                    </div>
                    <p className="text-sm font-bold text-slate-500 mb-1">{t("msg_no_tasks", { status: col === "Pending" ? t("status_pending") : col === "In Progress" ? t("status_in_progress") : col === "Completed" ? t("status_completed") : col })}</p>
                    <p className="text-xs text-slate-400">{t("msg_drag_task")}</p>
                  </div>
                )}
              </div>
              <button onClick={() => { setEditTask(null); setFormErrors({}); setForm({ title: "", description: "", assigned_to: "", priority: "Medium", status: col, due_date: "" }); setModal(true); }} className="w-full mt-2 py-3 text-sm font-bold text-slate-400 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl hover:border-primary/40 hover:text-primary transition-all cursor-pointer bg-white/50 dark:bg-slate-800/50 flex items-center justify-center gap-2">
                <Icon name="plus" size={14} /> {t("btn_add_task")}
              </button>
            </div>
          ))}
        </div>

        {/* Sliding Drawer for Closed Tasks */}
        <div className={`transition-all duration-300 ease-in-out flex flex-col bg-slate-100/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl shadow-sm ${closedVisible ? 'w-full lg:w-80 opacity-100 p-3 lg:border-t-4 border-t-slate-400 min-h-[400px]' : 'w-full lg:w-12 h-16 lg:h-auto lg:min-h-[400px] opacity-80 hover:opacity-100 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer p-0'} overflow-hidden relative flex-shrink-0`}
          onClick={() => !closedVisible && setClosedVisible(true)}
          onDragOver={e => { if (closedVisible) { e.preventDefault(); setDragOverCol("Closed"); } }}
          onDragLeave={() => closedVisible && setDragOverCol(null)}
          onDrop={e => closedVisible && onDrop(e, "Closed")}
        >
          {closedVisible ? (
            <>
              <div className="flex items-center justify-between mb-4 sticky top-0 z-10 bg-slate-100/95 dark:bg-slate-800/95 backdrop-blur-sm py-2 px-1 -mx-1">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-slate-400" />
                  <span className="text-sm font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("status_closed")}</span>
                  <span className="text-xs font-bold text-slate-500 bg-white dark:bg-slate-700 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-600 shadow-sm">{visibleClosedTasks.length}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setClosedVisible(false); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 bg-transparent border-0 cursor-pointer p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center">
                  <Icon name="chevron_up" size={18} className="lg:hidden" />
                  <Icon name="chevron_right" size={18} className="hidden lg:block" />
                </button>
              </div>
              <div className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden p-1 -m-1">
                {loading ? (
                  Array(2).fill(0).map((_, i) => (
                    <div key={`skel-closed-${i}`} className="bg-white/50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-3 animate-pulse">
                      <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3 mb-2" />
                      <div className="flex gap-1.5">
                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-10" />
                        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-12" />
                      </div>
                    </div>
                  ))
                ) : (
                  visibleClosedTasks.map(task => (
                    <div key={task.id} draggable
                      onDragStart={e => onDragStart(e, task.id)}
                      className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3 cursor-grab hover:border-slate-300 transition-all group opacity-70 hover:opacity-100 ${dragId === task.id ? "opacity-30" : ""}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-xs font-bold text-slate-500 flex-1 line-through">{task.title}</p>
                        <div className="flex items-center gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <Btn
                            xs
                            icon="edit"
                            onClick={(e) => { e.stopPropagation(); startEdit(task); }}
                            className="!p-0 w-7 h-7 rounded-lg flex items-center justify-center"
                            title={t("btn_edit", "Edit")}
                          />
                          <Btn
                            xs
                            variant="danger"
                            icon="trash"
                            onClick={(e) => { e.stopPropagation(); setDelTask(task); }}
                            className="!p-0 w-7 h-7 rounded-lg flex items-center justify-center"
                            title={t("btn_del", "Delete")}
                          />
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        <Badge color="gray" sm>{task.priority === "Low" ? t("opt_low") : task.priority === "Medium" ? t("opt_medium") : t("opt_high")}</Badge>
                        {task.due_date && <span className="text-xs text-slate-400 bg-slate-50 px-1.5 py-px rounded-md">📅 {formatDateByPreference(task.due_date)}</span>}
                      </div>
                    </div>
                  ))
                )}
                {!loading && visibleClosedTasks.length === 0 && (
                  <div className="py-8 text-center text-sm text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-700/50 rounded-2xl flex flex-col items-center gap-2">
                    <Icon name="archive" size={24} className="opacity-50" />
                    <span>{t("msg_no_closed")}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="h-full w-full flex flex-row lg:flex-col items-center justify-between lg:justify-center py-0 px-4 lg:px-0 lg:py-4 gap-4 lg:gap-6">
              <Icon name="chevron_down" size={18} className="text-slate-400 lg:hidden" />
              <Icon name="chevron_left" size={18} className="text-slate-400 hidden lg:block" />
              
              <div style={{ writingMode: "vertical-lr", textOrientation: "mixed", transform: "rotate(180deg)" }} className="hidden lg:flex text-sm font-black text-slate-400 uppercase tracking-widest whitespace-nowrap flex-1 justify-center">
                {t("label_closed_tasks")}
              </div>
              
              <div className="lg:hidden text-sm font-black text-slate-400 uppercase tracking-widest whitespace-nowrap flex-1 text-center">
                {t("label_closed_tasks")}
              </div>

              <div className="bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-xs font-bold px-2 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 shadow-sm">
                {visibleClosedTasks.length}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
